
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { addDays, differenceInDays, format } from 'date-fns';
import { fr } from 'date-fns/locale';

// Helper function to calculate next period date
const calculateNextPeriod = (lastPeriod: string, cycleLength: number) => {
  const lastPeriodDate = new Date(lastPeriod);
  return addDays(lastPeriodDate, cycleLength);
};

// Helper to calculate fertile window
const calculateFertileWindow = (nextPeriod: Date, cycleLength: number) => {
  const ovulationDay = addDays(nextPeriod, -14);
  return {
    start: addDays(ovulationDay, -5),
    end: addDays(ovulationDay, 1)
  };
};

export const PeriodTracker = () => {
  const { data: periodData, isLoading } = useQuery({
    queryKey: ["period-data"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;
      
      const { data, error } = await supabase
        .from("period_tracking")
        .select("*")
        .eq("user_id", user.id)
        .order("last_period_date", { ascending: false })
        .limit(1)
        .single();
        
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <Card>
          <CardHeader>
            <div className="h-7 bg-muted rounded w-1/3"></div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-4 bg-muted rounded"></div>
            <div className="h-32 bg-muted rounded"></div>
            <div className="h-24 bg-muted rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!periodData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Aucune donnée de cycle</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">
            Veuillez ajouter vos informations de cycle menstruel pour commencer le suivi.
          </p>
          <button
            className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring bg-primary text-primary-foreground shadow hover:bg-primary/90 h-9 px-4 py-2"
          >
            Commencer le suivi
          </button>
        </CardContent>
      </Card>
    );
  }

  const today = new Date();
  const cycleLength = periodData.cycle_length || 28;
  const nextPeriod = calculateNextPeriod(periodData.last_period_date, cycleLength);
  const daysUntilNextPeriod = differenceInDays(nextPeriod, today);
  const fertilityWindow = calculateFertileWindow(nextPeriod, cycleLength);
  const isFertile = today >= fertilityWindow.start && today <= fertilityWindow.end;
  
  // Determine current phase
  let currentPhase = "menstruelle";
  let phaseColor = "pink";
  
  if (daysUntilNextPeriod <= 0 && daysUntilNextPeriod > -5) {
    currentPhase = "menstruelle";
    phaseColor = "pink";
  } else if (isFertile) {
    currentPhase = "fertile";
    phaseColor = "emerald";
  } else if (daysUntilNextPeriod < 14) {
    currentPhase = "lutéale";
    phaseColor = "amber";
  } else {
    currentPhase = "folliculaire";
    phaseColor = "blue";
  }

  return (
    <div className="space-y-6">
      <Card className="overflow-hidden border-primary/20">
        <div className={`h-2 bg-${phaseColor}-400`}></div>
        <CardHeader>
          <CardTitle>
            Phase {currentPhase} - {daysUntilNextPeriod > 0 
              ? `${daysUntilNextPeriod} jours avant vos règles` 
              : daysUntilNextPeriod === 0 
              ? "Vos règles commencent aujourd'hui" 
              : `Jour ${Math.abs(daysUntilNextPeriod)} de vos règles`}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="relative bg-muted/30 rounded-lg h-24 overflow-hidden">
            <div className="absolute inset-0 flex">
              <div className="h-full w-1/4 bg-pink-100 flex items-center justify-center">
                <span className="text-xs font-medium text-pink-800">Menstruelle</span>
              </div>
              <div className="h-full w-1/4 bg-blue-100 flex items-center justify-center">
                <span className="text-xs font-medium text-blue-800">Folliculaire</span>
              </div>
              <div className="h-full w-1/4 bg-emerald-100 flex items-center justify-center">
                <span className="text-xs font-medium text-emerald-800">Fertile</span>
              </div>
              <div className="h-full w-1/4 bg-amber-100 flex items-center justify-center">
                <span className="text-xs font-medium text-amber-800">Lutéale</span>
              </div>
            </div>
            
            {/* Day indicator */}
            <div 
              className="absolute top-0 h-full border-l-2 border-primary" 
              style={{
                left: `${(Math.min(cycleLength - daysUntilNextPeriod, cycleLength) / cycleLength) * 100}%`
              }}
            >
              <div className="bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center -ml-[10px] mt-1">
                {Math.min(cycleLength - daysUntilNextPeriod, cycleLength)}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 p-4 border border-blue-100 dark:border-blue-800/30">
              <h4 className="text-sm font-medium">Prochaines règles</h4>
              <p className="text-lg font-semibold">
                {format(nextPeriod, 'PPP', { locale: fr })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Dans {daysUntilNextPeriod} jours
              </p>
            </div>
            
            <div className={`rounded-lg bg-${phaseColor}-50 dark:bg-${phaseColor}-900/20 p-4 border border-${phaseColor}-100 dark:border-${phaseColor}-800/30`}>
              <h4 className="text-sm font-medium">Phase actuelle</h4>
              <p className="text-lg font-semibold capitalize">
                {currentPhase}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {currentPhase === "menstruelle" 
                  ? "Repos et détente recommandés" 
                  : currentPhase === "fertile" 
                  ? "Période de fertilité élevée" 
                  : currentPhase === "lutéale" 
                  ? "Attention aux symptômes prémenstruels" 
                  : "Période d'énergie"}
              </p>
            </div>
          </div>
          
          <div className="rounded-lg bg-muted/30 p-4">
            <h4 className="font-medium mb-2">Période fertile</h4>
            <p className="text-sm">
              Du {format(fertilityWindow.start, 'PPP', { locale: fr })} au {format(fertilityWindow.end, 'PPP', { locale: fr })}
              {isFertile && " (Aujourd'hui)"}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
